import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SPREADSHEET_ID = "1S9jIRfwG0r01THQEO7pYYCQEgdkjbsTVqX0bQvqktNU";
const GATEWAY_URL = "https://connector-gateway.lovable.dev/google_sheets/v4";

const score = z.number().min(0).max(100).nullable();

const leadSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
  stage: z.string().trim().max(120),
  overall: score,
  dimensions: z.array(score).length(4),
  topGaps: z.array(z.string().trim().max(120)).max(3),
  scores: z.array(score).length(16),
  pdfBase64: z
    .string()
    .max(4_000_000)
    .regex(/^JVB[A-Za-z0-9+/=]*$/)
    .optional(),
});

async function emailResults(data: z.infer<typeof leadSchema>) {
  if (!data.pdfBase64) return;
  try {
    const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
    await sendTemplateEmail("assessment-results", data.email, {
      from: "Leadership Edge Live <support@leadershipedge.live>",
      replyTo: "support@leadershipedge.live",
      templateData: { name: data.name, stage: data.stage, topGaps: data.topGaps },
      attachments: [
        {
          filename: "leadership-gaps-assessment-results.pdf",
          contentBase64: data.pdfBase64,
          contentType: "application/pdf",
        },
      ],
    });
  } catch (error) {
    console.error("[assessment] results email failed", error);
  }
}

const fmt = (s: number | null) => (s == null ? "" : Math.round(s));

export const saveAssessmentLead = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => leadSchema.parse(data))
  .handler(async ({ data }) => {
    await emailResults(data);
    const lovableKey = process.env["LOVABLE_API_KEY"];
    const sheetsKey = process.env["GOOGLE_SHEETS_API_KEY"];
    if (!lovableKey || !sheetsKey) {
      console.error("Assessment lead not saved: Google Sheets is not configured");
      return { ok: false };
    }

    const now = new Date();
    const tz = "America/Phoenix";
    const date = now.toLocaleDateString("en-US", { timeZone: tz });
    const time = now.toLocaleTimeString("en-US", { timeZone: tz });

    const row = [
      data.name,
      data.email,
      "Leadership Gaps Assessment",
      date,
      time,
      data.stage,
      fmt(data.overall),
      ...data.dimensions.map(fmt),
      data.topGaps.join(", "),
      ...data.scores.map(fmt),
    ];

    const res = await fetch(
      `${GATEWAY_URL}/spreadsheets/${SPREADSHEET_ID}/values/Sheet1!A:AB:append?valueInputOption=RAW&insertDataOption=INSERT_ROWS`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${lovableKey}`,
          "X-Connection-Api-Key": sheetsKey,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ values: [row] }),
      },
    );
    if (!res.ok) {
      console.error(`Assessment lead append failed [${res.status}]: ${await res.text()}`);
      return { ok: false };
    }
    return { ok: true };
  });
